import { useState, useEffect, FC } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { endOfDay, isAfter, isFuture, startOfDay, subDays } from 'date-fns'

import Api from 'services/Api'
import { Format, Parse } from 'services/Dates'
import { getUserName, getPositionName, getProjectCode, hasAccessToThis } from 'services/Utilities'
import { formatPunchCardBreaks } from 'services/schedule/getBreaksToApply'
import { refreshCounter } from 'store/actions/refresh'
import { closeModal, openModal } from 'store/actions/modals'
import BaseModal from 'components/Modals/BaseModal'
import { Button } from 'components/Forms'
import { Loading } from 'components/UI'
import { Alert, Badge, Button as MerinioButton, Card, List, Checkbox } from 'ui'

import { t } from 'translations'

const UnpunchedShiftsModal: FC<{ filter: { rangeStart: string; rangeEnd: string } }> = ({ filter }): JSX.Element => {
  const dispatch = useDispatch()
  const { autoApproveUnpunchedShifts, punchCardLockDate } = useSelector((state: MStore.RootState) => state.settings)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [events, setEvents] = useState<MData.Event[]>([])
  const [errorLoading, setErrorLoading] = useState(false)
  const [selectedEvents, setSelectedEvents] = useState({})
  const [errorCreatingCards, setErrorCreatingCards] = useState(false)
  const [createForAbsenceTimeOff, setCreateForAbsenceTimeOff] = useState(false)

  useEffect(() => {
    const fetchEvents = async () => {
      const processedRangeEnd = isFuture(new Date(filter.rangeEnd)) ? new Date() : filter.rangeEnd
      const { result, success } = await Api.post('schedule/schedule/query', {
        ...filter,
        rangeStart: Format(startOfDay(Parse(filter.rangeStart, { inFormat: 'date' }) || subDays(new Date(), 28)), { outFormat: 'server' }),
        rangeEnd: Format(endOfDay(Parse(processedRangeEnd, { inFormat: 'date' }) || subDays(new Date(), 1)), { outFormat: 'server' }),
        noPunchCard: true,
        markShiftsForLeaveOrVacation: true
      })
      if (!success || !result.length) return setErrorLoading(true)
      setEvents(result)

      // If this setting is active, auto select the shifts in the modal, this is used to quickly create punch cards from the schedule when employees do not punch
      if (autoApproveUnpunchedShifts) setSelectedEvents(result.reduce((acc, { _id }) => ({ ...acc, [_id]: true }), {}))
    }
    fetchEvents()
  }, [filter, autoApproveUnpunchedShifts])

  // Function that creates a punch card for every selected shift
  const createPunchCards = async () => {
    setIsSubmitting(true)

    let hadError = false

    for (const event of events.filter(({ isAbsent, isOnTimeoff }) => createForAbsenceTimeOff || (!isAbsent && !isOnTimeoff))) {
      if (!selectedEvents[event._id] || !event.employeeId) continue
      const punchCard: Partial<MData.PunchCard> = {
        additionalInfo: '',
        approvedManually: false,
        arrival: event.start,
        billingDetails: event.billingDetails || {},
        breaks: formatPunchCardBreaks({ arrival: Parse(event.start), leave: Parse(event.end), breaksToApply: event.breaks, outFormat: 'server' }) || [],
        disableArrivalRounding: false,
        disableLeaveRounding: false,
        end: event.end,
        leave: event.end,
        positionId: event.positionId,
        projectId: event.projects?.[0],
        teamId: event.team,
        shiftId: event._id,
        tags: event.tags,
        start: event.start,
        status: autoApproveUnpunchedShifts ? 'APPROVED' : 'APPROVAL_REQUIRED',
        timezone: event.startTimezone,
        transfers: [],
        userId: event.employeeId,
        wagePremiums: event.wagePremiums || []
      }

      const { success } = await Api.post('punch/cards', punchCard)
      if (!success) hadError = true
    }

    dispatch(refreshCounter('punchCards'))
    setIsSubmitting(false)

    if (!hadError) return dispatch(closeModal())

    setErrorCreatingCards(true)
  }

  if (!events && !errorLoading) return <Loading />

  const filteredEvents = events.filter(({ isAbsent, isOnTimeoff }) => createForAbsenceTimeOff || (!isAbsent && !isOnTimeoff))

  return (
    <BaseModal hideButtons title={`manager.punch.CREATE_PUNCH_CARDS_MODAL`}>
      {errorLoading ? (
        <Alert variant="warning" className="mt-2">
          {t(`manager.punch.CREATE_PUNCH_CARDS_MODAL.ERROR_LOADING`)}
        </Alert>
      ) : (
        <>
          <Alert variant="warning" className="mt-2">
            {t(`manager.punch.CREATE_PUNCH_CARDS_MODAL.DESCRIPTION`)}
          </Alert>
          <Alert variant="danger" className="mt-2" onClose={() => setErrorCreatingCards(false)} show={!!errorCreatingCards}>
            {t(`manager.punch.CREATE_PUNCH_CARDS_MODAL.ERROR_CREATING_CARDS`)}
          </Alert>
          <Card className="mb-1">
            <Card.Body className="bg-light">
              <div className="form-check">
                <Checkbox id="filterOnTimeOffAbsent" checked={createForAbsenceTimeOff} onChange={() => setCreateForAbsenceTimeOff(!createForAbsenceTimeOff)} />
                <label className="form-check-label" htmlFor="filterOnTimeOffAbsent">
                  {t('manager.punch.CREATE_PUNCH_CARDS_MODAL.CREATEFORABSENCETIMEOFF')}
                </label>
              </div>
            </Card.Body>
          </Card>

          {filteredEvents.length > 0 ? (
            <Card maxHeight="30rem" className="mb-1">
              <List className="list-group-flush">
                {filteredEvents.map(({ _id, employeeId, start, end, isAbsent, isOnTimeoff, positionId, projects }) => {
                  const cardIsLocked = isAfter(endOfDay(Parse(punchCardLockDate)), Parse(end)) && !hasAccessToThis('punchCardManageLocked')
                  return (
                    <List.Item className="d-flex" key={_id}>
                      <div>
                        {employeeId ? getUserName(employeeId, { showEmployeeNumber: true }) : null}
                        <div>
                          <div>
                            <b className="mr-1">
                              {Format(start, { outFormat: 'time' })} - {Format(end, { outFormat: 'time' })}
                            </b>
                            <i>{Format(start, { outFormat: 'humanDateCompact' })}</i>
                          </div>
                          <i>{getPositionName(positionId)}</i>

                          {hasAccessToThis('projectSee') && projects?.[0] ? (
                            <span
                              className="ml-1 text-link"
                              onClick={() => dispatch(openModal('EmployeeScheduleProjectInformationModal', { projectId: projects[0] }))}
                            >
                              <u>[{getProjectCode(projects[0])}]</u>
                            </span>
                          ) : null}
                        </div>
                        {cardIsLocked ? <Badge variant="danger">{t(`manager.punch.CREATE_PUNCH_CARDS_MODAL.LOCKEDPUNCHCARD`)}</Badge> : null}
                        {isAbsent ? <Badge variant="danger">{t(`manager.punch.CREATE_PUNCH_CARDS_MODAL.ISABSENT`)}</Badge> : null}
                        {isOnTimeoff ? <Badge variant="complimentary">{t(`manager.punch.CREATE_PUNCH_CARDS_MODAL.ISONTIMEOFF`)}</Badge> : null}
                      </div>
                      <div className="ml-auto">
                        <MerinioButton
                          size="small"
                          className={`${selectedEvents[_id] ? 'btn-success' : 'btn-outline-secondary'}`}
                          color={selectedEvents[_id] ? 'success' : 'secondary'}
                          outlined={!selectedEvents[_id]}
                          onClick={() => setSelectedEvents({ ...selectedEvents, [_id]: !selectedEvents[_id] })}
                          disabled={cardIsLocked}
                        >
                          <i className={`${selectedEvents[_id] ? 'fas fa-check-circle' : 'far fa-circle'}`} />
                        </MerinioButton>
                      </div>
                    </List.Item>
                  )
                })}
              </List>
            </Card>
          ) : null}
        </>
      )}

      <div className="mt-2">
        {errorLoading ? null : (
          <Button
            text="actions.CONFIRM"
            disabled={errorCreatingCards || !Object.values(selectedEvents).filter(Boolean).length}
            isSubmitting={isSubmitting}
            className="btn btn-primary mr-2"
            onClick={() => createPunchCards()}
          />
        )}
        <Button text="actions.CLOSE" className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())} />
      </div>
    </BaseModal>
  )
}

export default UnpunchedShiftsModal
